import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BarChart3,
  Eye,
  FileText,
  Loader2,
  MoreVertical,
  Search,
  X,
} from 'lucide-react';
import WhatsAppPanelShell from './WhatsAppPanelShell';
import WhatsAppDevicePreview from './WhatsAppDevicePreview';
import WhatsAppStatsPeriodSelector from './WhatsAppStatsPeriodSelector';
import { notificationPreviewFromTemplate } from './whatsappTemplatePreview';
import { previewDeepLinkFromCampaign } from './whatsappDeepLinkPreview';
import NotificationDeepLinkPicker from './NotificationDeepLinkPicker';
import WhatsAppTemplateVariableMappingEditor from './WhatsAppTemplateVariableMappingEditor';
import { mergeNotificationFields } from './whatsappNotificationFields';
import WhatsAppMediaHeaderUploader from './WhatsAppMediaHeaderUploader';
import WhatsAppTemplateButtonsPreview from './WhatsAppTemplateButtonsPreview';
import { buildVariableMappings, resolveMappingSource } from './whatsappVariableMappings';
import { parseConsecutiveTemplateSlots } from './whatsappTemplateSlots';
import { fetchDeepLinkCampaigns } from '../../services/MasterAdmin/deepLinkService';
import {
  buildStatsQueryParams,
  isCustomStatsReady,
  statsMessagesLabel,
  type WhatsAppStatsQuery,
} from './whatsappStatsPeriod';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import type {
  WhatsAppNotificationCatalog,
  WhatsAppNotificationEvent,
  WhatsAppTemplateVariableMapping,
  WhatsAppContextField,
  WhatsAppCtaDeepLinkConfig,
  WhatsAppTemplateRule,
} from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';
import { isActiveWhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import {
  fetchWhatsAppNotificationCatalog,
  fetchWhatsAppNotificationDetail,
  fetchWhatsAppTemplateList,
  fetchWhatsAppHubSettings,
  updateWhatsAppNotificationEvent,
} from '../../services/MasterAdmin/whatsappHubService';

type AudienceTab = 'user' | 'service_provider';

function formatInr(value: number) {
  return `₹${value.toFixed(3)}`;
}

function isServiceProviderRecipient(recipientType: string) {
  return ['vendor', 'driver', 'admin', 'phone'].includes(recipientType);
}

function getActiveTemplates(templates: WhatsAppTemplate[]) {
  return templates.filter((t) => isActiveWhatsAppTemplate(t));
}

function activeTemplateNameSet(templates: WhatsAppTemplate[]) {
  return new Set(getActiveTemplates(templates).map((t) => t.name));
}

function buildActiveTemplateMap(templates: WhatsAppTemplate[]) {
  const map = new Map<string, WhatsAppTemplate>();
  for (const t of getActiveTemplates(templates)) {
    map.set(t.name, t);
  }
  return map;
}

/** Only show assigned template when it exists and is active on Interakt. */
function resolveAssignedTemplateName(
  item: Pick<WhatsAppNotificationEvent, 'templateName'>,
  activeNames: Set<string>
): string | null {
  const name = item.templateName?.trim();
  if (!name || !activeNames.has(name)) return null;
  return name;
}

function hasAssignableWhatsAppTemplate(
  item: Pick<WhatsAppNotificationEvent, 'templateName' | 'templateRules' | 'defaultTemplateName'>,
  activeNames: Set<string>
): boolean {
  if (resolveAssignedTemplateName(item, activeNames)) return true;
  const fallback = item.defaultTemplateName?.trim();
  if (fallback && activeNames.has(fallback)) return true;
  return (item.templateRules || []).some((rule) => {
    const name = rule.templateName?.trim();
    return rule.enabled !== false && Boolean(name) && activeNames.has(name as string);
  });
}

function assignedTemplateLabel(
  item: Pick<WhatsAppNotificationEvent, 'templateName' | 'templateRules' | 'defaultTemplateName'>,
  activeNames: Set<string>
): { text: string; missing: boolean } {
  const assigned = resolveAssignedTemplateName(item, activeNames);
  if (assigned) {
    const extra =
      item.templateRules && item.templateRules.length > 0 ? ` + ${item.templateRules.length} rule(s)` : '';
    return { text: `${assigned}${extra}`, missing: false };
  }
  const fallback = item.defaultTemplateName?.trim();
  if (fallback && activeNames.has(fallback)) {
    return { text: fallback, missing: false };
  }
  const validRules = (item.templateRules || []).filter((rule) => {
    const name = rule.templateName?.trim();
    return rule.enabled !== false && Boolean(name) && activeNames.has(name as string);
  });
  if (validRules.length) {
    return { text: `${validRules.length} rule(s)`, missing: false };
  }
  const stored = item.templateName?.trim() || fallback;
  if (stored) return { text: `${stored} (not found)`, missing: true };
  return { text: '—', missing: false };
}

function newTemplateRule(partial?: Partial<WhatsAppTemplateRule>): WhatsAppTemplateRule {
  return {
    id: `rule_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    label: partial?.label || 'Online',
    enabled: partial?.enabled !== false,
    templateName: partial?.templateName || '',
    languageCode: partial?.languageCode || null,
    templateVariableMappings: partial?.templateVariableMappings || [],
    conditions: partial?.conditions || [
      { field: 'consultationType', operator: 'contains', value: 'Online' },
    ],
  };
}

function RuleTemplateBlock({
  rule,
  index,
  activeTemplates,
  activeTemplateByName,
  contextFields,
  fieldsLoading,
  deepLinkCampaigns,
  campaignsLoading,
  onCampaignCreated,
  onChangeRule,
  onRemove,
}: {
  rule: WhatsAppTemplateRule;
  index: number;
  activeTemplates: WhatsAppTemplate[];
  activeTemplateByName: Map<string, WhatsAppTemplate>;
  contextFields: WhatsAppContextField[];
  fieldsLoading: boolean;
  deepLinkCampaigns: DeepLinkCampaign[];
  campaignsLoading: boolean;
  onCampaignCreated: (c: DeepLinkCampaign) => void;
  onChangeRule: (index: number, next: WhatsAppTemplateRule) => void;
  onRemove: (index: number) => void;
}) {
  const ruleTemplate = rule.templateName
    ? activeTemplateByName.get(rule.templateName)
    : undefined;
  const ruleSlotInfo = useMemo(
    () => parseConsecutiveTemplateSlots(ruleTemplate?.bodyPreview || ''),
    [ruleTemplate?.bodyPreview]
  );
  const ruleVarCount = Math.max(
    ruleSlotInfo.count,
    ruleTemplate?.variableCount ?? 0,
    rule.templateVariableMappings?.length ?? 0
  );

  useEffect(() => {
    if (!rule.templateName || !ruleTemplate) return;
    const existing = rule.templateVariableMappings || [];
    const filled = existing.filter((m) => {
      if (m.sourceType === 'static') return Boolean(String(m.staticValue || '').trim());
      if (m.sourceType === 'deep_link') return Boolean(m.deepLinkCampaignId);
      return Boolean(String(m.contextKey || '').trim());
    });
    if (filled.length > 0) return;
    const source = resolveMappingSource(
      undefined,
      ruleTemplate.defaultVariableMappings
    );
    const built = buildVariableMappings(ruleVarCount, source, contextFields);
    if (!built.length) return;
    onChangeRule(index, { ...rule, templateVariableMappings: built });
    // Only re-seed when template changes or fields finish loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule.templateName, ruleVarCount, fieldsLoading, contextFields.length]);

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-800/50">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={rule.label}
          onChange={(e) => onChangeRule(index, { ...rule, label: e.target.value })}
          placeholder="Rule label"
          className="min-w-[120px] flex-1 rounded border px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
        />
        <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
          <input
            type="checkbox"
            checked={rule.enabled !== false}
            onChange={(e) => onChangeRule(index, { ...rule, enabled: e.target.checked })}
          />
          On
        </label>
        <button type="button" onClick={() => onRemove(index)} className="text-xs text-red-600">
          Remove
        </button>
      </div>

      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <select
          value={rule.conditions[0]?.field || 'consultationType'}
          onChange={(e) =>
            onChangeRule(index, {
              ...rule,
              conditions: [
                {
                  ...(rule.conditions[0] || { operator: 'contains', value: '' }),
                  field: e.target.value,
                },
              ],
            })
          }
          className="rounded border px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
        >
          <option value="consultationType">Consultation type</option>
          <option value="speciality">Speciality</option>
          <option value="cancelledBy">Cancelled by</option>
        </select>
        <select
          value={rule.conditions[0]?.operator || 'contains'}
          onChange={(e) =>
            onChangeRule(index, {
              ...rule,
              conditions: [
                {
                  ...(rule.conditions[0] || { field: 'consultationType', value: '' }),
                  operator: e.target
                    .value as WhatsAppTemplateRule['conditions'][0]['operator'],
                },
              ],
            })
          }
          className="rounded border px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
        >
          <option value="contains">contains</option>
          <option value="eq">equals</option>
          <option value="neq">not equals</option>
          <option value="starts_with">starts with</option>
        </select>
        <input
          value={
            Array.isArray(rule.conditions[0]?.value)
              ? rule.conditions[0]?.value.join(',')
              : rule.conditions[0]?.value || ''
          }
          onChange={(e) =>
            onChangeRule(index, {
              ...rule,
              conditions: [
                {
                  ...(rule.conditions[0] || {
                    field: 'consultationType',
                    operator: 'contains',
                  }),
                  value: e.target.value,
                },
              ],
            })
          }
          placeholder="e.g. Online or Offline"
          className="rounded border px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
        />
      </div>

      <select
        value={rule.templateName}
        onChange={(e) =>
          onChangeRule(index, {
            ...rule,
            templateName: e.target.value,
            templateVariableMappings: [],
          })
        }
        className="mt-2 w-full rounded border px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
      >
        <option value="">Select template for this rule…</option>
        {activeTemplates.map((t) => (
          <option key={t.name} value={t.name}>
            {t.displayName || t.name}
          </option>
        ))}
      </select>

      {ruleTemplate?.bodyPreview && (
        <div className="mt-3">
          <p className="text-xs font-medium uppercase text-gray-400">Rule template preview</p>
          <p className="mt-1 whitespace-pre-wrap rounded-lg bg-white p-3 text-sm leading-relaxed dark:bg-gray-900">
            {ruleTemplate.bodyPreview}
          </p>
        </div>
      )}

      {rule.templateName && (
        <div className="mt-3">
          <p className="mb-2 text-sm font-medium">
            Rule variables ({ruleVarCount})
          </p>
          {fieldsLoading ? (
            <p className="text-sm text-gray-500">Loading field names…</p>
          ) : (
            <>
              {campaignsLoading && (
                <p className="mb-2 text-xs text-gray-400">Loading deep link campaigns…</p>
              )}
              <WhatsAppTemplateVariableMappingEditor
                mappings={buildVariableMappings(
                  ruleVarCount,
                  rule.templateVariableMappings,
                  contextFields
                )}
                fields={contextFields}
                campaigns={deepLinkCampaigns}
                onCampaignCreated={onCampaignCreated}
                onChange={(mappings) =>
                  onChangeRule(index, { ...rule, templateVariableMappings: mappings })
                }
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Template category (UTILITY / MARKETING / etc.) when an active template is assigned. */
function resolveAssignedTemplateCategory(
  item: Pick<WhatsAppNotificationEvent, 'templateName'>,
  activeNames: Set<string>,
  templateByName: Map<string, WhatsAppTemplate>
): string | null {
  const name = resolveAssignedTemplateName(item, activeNames);
  if (!name) return null;
  const category = templateByName.get(name)?.category?.trim();
  return category || null;
}

function patchCatalogNotification(
  catalog: WhatsAppNotificationCatalog,
  eventKey: string,
  patch: Partial<WhatsAppNotificationEvent>
): WhatsAppNotificationCatalog {
  const apply = (item: WhatsAppNotificationEvent): WhatsAppNotificationEvent => {
    if (item.eventKey !== eventKey) return item;
    const whatsappEnabled = patch.whatsappEnabled ?? item.whatsappEnabled;
    return {
      ...item,
      ...patch,
      whatsappEnabled,
      enabled: whatsappEnabled,
    };
  };

  const items = catalog.items.map(apply);
  const userCount = items.filter((i) => i.recipientType === 'user').length;
  const providerCount = items.filter((i) => isServiceProviderRecipient(i.recipientType)).length;

  return {
    ...catalog,
    items,
    tabs: catalog.tabs.map((t) => ({
      ...t,
      count: t.id === 'user' ? userCount : t.id === 'service_provider' ? providerCount : t.count,
    })),
    totals: {
      ...catalog.totals,
      enabledEvents: items.filter((i) => i.whatsappEnabled).length,
    },
  };
}

function ToggleSwitch({
  checked,
  disabled,
  onChange,
  title,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      title={title}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
        disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
      } ${checked ? 'bg-[#25D366]' : 'bg-gray-300 dark:bg-gray-600'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

type HealthTriState = 'untested' | 'working' | 'not_working';
type HealthFilter = 'all' | 'untested' | 'working' | 'issues';
type NotificationHealthPatch = {
  templateWorking?: boolean | null;
  linkNavigationWorking?: boolean | null;
};

function healthToTriState(value: unknown): HealthTriState {
  if (value === true || value === 1 || value === '1' || value === 'working' || value === 'true') {
    return 'working';
  }
  if (value === false || value === 0 || value === '0' || value === 'not_working' || value === 'false') {
    return 'not_working';
  }
  return 'untested';
}

function triStateToHealth(value: HealthTriState): boolean | null {
  if (value === 'working') return true;
  if (value === 'not_working') return false;
  return null;
}

function matchesHealthFilter(item: WhatsAppNotificationEvent, filter: HealthFilter) {
  if (filter === 'all') return true;
  const template = healthToTriState(item.templateWorking);
  const link = healthToTriState(item.linkNavigationWorking);
  if (filter === 'untested') return template === 'untested' || link === 'untested';
  if (filter === 'working') return template === 'working' && link === 'working';
  return template === 'not_working' || link === 'not_working';
}

function formatHealthCheckedAt(iso?: string | null) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

function HealthTriStateSelect({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: unknown;
  disabled?: boolean;
  onChange: (next: boolean | null) => void;
}) {
  const tri = healthToTriState(value);
  const tone =
    tri === 'working'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
      : tri === 'not_working'
        ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200'
        : 'border-gray-200 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';
  return (
    <label className="block min-w-[118px]">
      <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </span>
      <select
        value={tri}
        disabled={disabled}
        onChange={(e) => onChange(triStateToHealth(e.target.value as HealthTriState))}
        className={`w-full rounded-md border px-1.5 py-1 text-xs ${tone} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        <option value="untested">Untested</option>
        <option value="working">Working</option>
        <option value="not_working">Not working</option>
      </select>
    </label>
  );
}

function NotificationHealthCell({
  item,
  disabled,
  onChange,
}: {
  item: WhatsAppNotificationEvent;
  disabled?: boolean;
  onChange: (patch: NotificationHealthPatch) => void;
}) {
  return (
    <div className="flex min-w-[132px] flex-col gap-1.5">
      <HealthTriStateSelect
        label="Template"
        value={item.templateWorking}
        disabled={disabled}
        onChange={(templateWorking) => onChange({ templateWorking })}
      />
      <HealthTriStateSelect
        label="Link"
        value={item.linkNavigationWorking}
        disabled={disabled}
        onChange={(linkNavigationWorking) => onChange({ linkNavigationWorking })}
      />
    </div>
  );
}

function RowActionsMenu({
  item,
  onView,
  onTemplate,
  onToggle,
  toggling,
  canEnable,
}: {
  item: WhatsAppNotificationEvent;
  onView: () => void;
  onTemplate: () => void;
  onToggle: (enabled: boolean) => void;
  toggling: boolean;
  canEnable: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
        aria-label="Actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 min-w-[180px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onView();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Eye className="h-4 w-4 text-gray-400" />
            View details
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onTemplate();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <FileText className="h-4 w-4 text-gray-400" />
            Set template
          </button>
          <Link
            to={`/operations/whatsapp/analytics?focused=1&eventType=${encodeURIComponent(item.eventKey)}${
              item.templateName ? `&templateName=${encodeURIComponent(item.templateName)}` : ''
            }`}
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <BarChart3 className="h-4 w-4 text-gray-400" />
            Analytics
          </Link>
          <div className="my-1 border-t border-gray-100 dark:border-gray-800" />
          <button
            type="button"
            disabled={toggling || (!item.whatsappEnabled && !canEnable)}
            title={
              !item.whatsappEnabled && !canEnable
                ? 'Assign an approved template before enabling WhatsApp'
                : undefined
            }
            onClick={() => {
              setOpen(false);
              onToggle(!item.whatsappEnabled);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-50 dark:hover:bg-gray-800"
          >
            {item.whatsappEnabled ? 'Disable WhatsApp' : 'Enable WhatsApp'}
          </button>
        </div>
      )}
    </div>
  );
}

function NotificationViewModal({
  item,
  templates,
  statsQuery,
  onStatsQueryChange,
  onClose,
  onSaved,
}: {
  item: WhatsAppNotificationEvent;
  templates: WhatsAppTemplate[];
  statsQuery: WhatsAppStatsQuery;
  onStatsQueryChange: (next: WhatsAppStatsQuery) => void;
  onClose: () => void;
  onSaved: (patch: Partial<WhatsAppNotificationEvent>) => void;
}) {
  const activeNames = useMemo(() => activeTemplateNameSet(templates), [templates]);
  const activeTemplates = useMemo(() => getActiveTemplates(templates), [templates]);
  const activeTemplateByName = useMemo(() => buildActiveTemplateMap(templates), [templates]);

  const [detail, setDetail] = useState<WhatsAppNotificationEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deepLinkCampaigns, setDeepLinkCampaigns] = useState<DeepLinkCampaign[]>([]);
  const [whatsappEnabled, setWhatsappEnabled] = useState(item.whatsappEnabled);
  const [templateName, setTemplateName] = useState(
    () => resolveAssignedTemplateName(item, activeNames) || ''
  );
  const [healthSaving, setHealthSaving] = useState(false);

  useEffect(() => {
    fetchDeepLinkCampaigns()
      .then(setDeepLinkCampaigns)
      .catch(() => setDeepLinkCampaigns([]));
  }, []);

  useEffect(() => {
    if (!isCustomStatsReady(statsQuery)) return;
    setLoading(true);
    fetchWhatsAppNotificationDetail(item.eventKey, buildStatsQueryParams(statsQuery))
      .then((r) => {
        setDetail(r.data);
        setWhatsappEnabled(r.data.whatsappEnabled);
        setTemplateName(resolveAssignedTemplateName(r.data, activeNames) || '');
      })
      .catch(() => {
        setDetail(item);
        setTemplateName(resolveAssignedTemplateName(item, activeNames) || '');
      })
      .finally(() => setLoading(false));
  }, [item.eventKey, statsQuery, activeNames]);

  const data = detail || item;
  const statsLabel = statsMessagesLabel(statsQuery);
  const campaignsById = useMemo(
    () => new Map(deepLinkCampaigns.map((c) => [c.id, c])),
    [deepLinkCampaigns]
  );
  const previewTemplate = templateName ? activeTemplateByName.get(templateName) : null;
  const templatePreviewProps = useMemo(() => {
    if (!previewTemplate) return null;
    return notificationPreviewFromTemplate(
      previewTemplate,
      data.templateVariableMappings,
      campaignsById
    );
  }, [previewTemplate, data.templateVariableMappings, campaignsById]);
  const templateCategory = useMemo(() => {
    if (templateName && activeNames.has(templateName)) {
      return activeTemplateByName.get(templateName)?.category?.trim() || null;
    }
    return resolveAssignedTemplateCategory(data, activeNames, activeTemplateByName);
  }, [templateName, activeNames, activeTemplateByName, data]);

  const deepLinkRefs = useMemo(() => {
    const refs: Array<{ label: string; campaign: DeepLinkCampaign | null; url: string }> = [];
    for (const m of data.templateVariableMappings || []) {
      if (m.sourceType !== 'deep_link' || !m.deepLinkCampaignId) continue;
      const campaign = campaignsById.get(m.deepLinkCampaignId) || null;
      const url = campaign
        ? previewDeepLinkFromCampaign(
            {
              deepLinkCampaignId: m.deepLinkCampaignId,
              deepLinkUrlType: m.deepLinkUrlType,
              deepLinkParamBindings: m.deepLinkParamBindings,
            },
            campaignsById
          )
        : '';
      refs.push({
        label: `Variable {{${m.slot}}}${m.label ? ` · ${m.label}` : ''}`,
        campaign,
        url,
      });
    }
    if (data.whatsappCtaConfig?.deepLinkCampaignId) {
      const campaign = campaignsById.get(data.whatsappCtaConfig.deepLinkCampaignId) || null;
      const url = campaign
        ? previewDeepLinkFromCampaign(
            {
              deepLinkCampaignId: data.whatsappCtaConfig.deepLinkCampaignId,
              deepLinkUrlType: data.whatsappCtaConfig.urlType,
              deepLinkParamBindings: data.whatsappCtaConfig.paramBindings,
            },
            campaignsById
          )
        : '';
      refs.push({
        label: `CTA button #${data.whatsappCtaConfig.buttonIndex}`,
        campaign,
        url,
      });
    }
    return refs;
  }, [data.templateVariableMappings, data.whatsappCtaConfig, campaignsById]);

  const handleHealthChange = async (patch: NotificationHealthPatch) => {
    if (healthSaving) return;
    const previous = {
      templateWorking: data.templateWorking ?? null,
      linkNavigationWorking: data.linkNavigationWorking ?? null,
      templateHealthCheckedAt: data.templateHealthCheckedAt ?? null,
      linkHealthCheckedAt: data.linkHealthCheckedAt ?? null,
    };
    const next = {
      ...patch,
      ...(patch.templateWorking !== undefined
        ? {
            templateHealthCheckedAt:
              patch.templateWorking === null ? null : new Date().toISOString(),
          }
        : {}),
      ...(patch.linkNavigationWorking !== undefined
        ? {
            linkHealthCheckedAt:
              patch.linkNavigationWorking === null ? null : new Date().toISOString(),
          }
        : {}),
    };
    setHealthSaving(true);
    setDetail((prev) => ({ ...(prev || data), ...next }));
    onSaved(next);
    try {
      await updateWhatsAppNotificationEvent(data.eventKey, patch);
    } catch (err: unknown) {
      setDetail((prev) => ({ ...(prev || data), ...previous }));
      onSaved(previous);
      toast.error(getApiErrorMessage(err, 'Failed to save health'));
    } finally {
      setHealthSaving(false);
    }
  };

  const handleSave = async () => {
    if (whatsappEnabled && !templateName.trim()) {
      toast.error('Assign a template before enabling WhatsApp');
      return;
    }
    const templateChanged =
      (templateName || null) !== (resolveAssignedTemplateName(data, activeNames) || null);
    const healthReset = templateChanged
      ? { templateWorking: null, linkNavigationWorking: null }
      : {};
    setSaving(true);
    try {
      await updateWhatsAppNotificationEvent(data.eventKey, {
        whatsappEnabled,
        templateName: templateName || null,
        ...healthReset,
      });
      toast.success(templateChanged ? 'Saved — re-test template and link health' : 'Saved');
      onSaved({
        whatsappEnabled,
        enabled: whatsappEnabled,
        templateName: templateName || null,
        ...healthReset,
        ...(templateChanged
          ? { templateHealthCheckedAt: null, linkHealthCheckedAt: null }
          : {}),
      });
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const detailsBody = (
    <>
      <dl className="grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium uppercase text-gray-400">Service</dt>
          <dd className="mt-1 text-sm">{data.service}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase text-gray-400">Audience</dt>
          <dd className="mt-1 text-sm capitalize">{data.recipientType}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase text-gray-400">FCM type</dt>
          <dd className="mt-1 font-mono text-xs">{data.fcmType || '—'}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase text-gray-400">Type</dt>
          <dd className="mt-1 text-sm">{templateCategory || '—'}</dd>
        </div>
      </dl>

      <div>
        <dt className="text-xs font-medium uppercase text-gray-400">Trigger</dt>
        <dd className="mt-1 text-sm text-gray-700 dark:text-gray-300">{data.trigger}</dd>
      </div>

      <div>
        <dt className="text-xs font-medium uppercase text-gray-400">Sample message</dt>
        <dd className="mt-1 rounded-lg bg-gray-50 p-3 text-sm leading-relaxed dark:bg-gray-800">
          {data.sampleMessage}
        </dd>
      </div>

      {data.bodyValueKeys.length > 0 && (
        <div>
          <dt className="text-xs font-medium uppercase text-gray-400">Template variables</dt>
          <dd className="mt-1 text-sm">{data.bodyValueKeys.join(', ')}</dd>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-medium uppercase text-gray-400">Usage stats</p>
          <WhatsAppStatsPeriodSelector value={statsQuery} onChange={onStatsQueryChange} compact />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className="text-xs text-gray-500">{statsLabel}</p>
            <p className="text-lg font-semibold">{data.stats.billableCount}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Failed</p>
            <p className="text-lg font-semibold">{data.stats.failed}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Est. cost</p>
            <p className="text-lg font-semibold">{formatInr(data.stats.estimatedCostInr)}</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">WhatsApp</p>
            <p className="text-xs text-gray-500">Send this notification on WhatsApp when enabled</p>
          </div>
          <ToggleSwitch
            checked={whatsappEnabled}
            disabled={!templateName.trim() && !whatsappEnabled}
            onChange={(next) => {
              if (next && !templateName.trim()) {
                toast.error('Assign a template before enabling WhatsApp');
                return;
              }
              setWhatsappEnabled(next);
            }}
          />
        </div>

        <div className="mt-4">
          <label className="text-xs font-medium uppercase text-gray-400">Interakt template</label>
          <select
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">Select template…</option>
            {activeTemplates.map((t) => (
              <option key={t.name} value={t.name}>
                {t.displayName || t.name}
              </option>
            ))}
          </select>
          {data.defaultTemplateName && activeNames.has(data.defaultTemplateName) && (
            <p className="mt-1 text-xs text-gray-400">Suggested: {data.defaultTemplateName}</p>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <p className="font-medium text-gray-900 dark:text-white">QA health</p>
        <p className="mt-0.5 text-xs text-gray-500">
          Mark after you test the live template send and that the CTA / deep link opens the right screen.
        </p>
        <div className="mt-3 flex flex-wrap gap-4">
          <HealthTriStateSelect
            label="Template working"
            value={data.templateWorking}
            disabled={healthSaving}
            onChange={(templateWorking) => void handleHealthChange({ templateWorking })}
          />
          <HealthTriStateSelect
            label="Link navigation"
            value={data.linkNavigationWorking}
            disabled={healthSaving}
            onChange={(linkNavigationWorking) => void handleHealthChange({ linkNavigationWorking })}
          />
        </div>
        <div className="mt-2 space-y-0.5 text-[11px] text-gray-400">
          {formatHealthCheckedAt(data.templateHealthCheckedAt) ? (
            <p>Template checked {formatHealthCheckedAt(data.templateHealthCheckedAt)}</p>
          ) : null}
          {formatHealthCheckedAt(data.linkHealthCheckedAt) ? (
            <p>Link checked {formatHealthCheckedAt(data.linkHealthCheckedAt)}</p>
          ) : null}
        </div>
      </div>

      {data.recentDeliveries && data.recentDeliveries.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold">Recent WhatsApp deliveries</h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Template</th>
                  <th className="px-3 py-2 text-left">Cost</th>
                  <th className="px-3 py-2 text-left">At</th>
                </tr>
              </thead>
              <tbody>
                {data.recentDeliveries.map((d) => (
                  <tr key={d.id} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="px-3 py-2 capitalize">{d.status}</td>
                    <td className="px-3 py-2">{d.templateName || '—'}</td>
                    <td className="px-3 py-2">{formatInr(d.estimatedCostInr)}</td>
                    <td className="px-3 py-2">{new Date(d.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
        <button
          type="button"
          disabled={saving || (whatsappEnabled && !templateName)}
          onClick={() => void handleSave()}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <Link
          to={`/operations/whatsapp/analytics?focused=1&eventType=${encodeURIComponent(data.eventKey)}${
            data.templateName || templateName
              ? `&templateName=${encodeURIComponent(data.templateName || templateName)}`
              : ''
          }`}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium dark:border-gray-600"
        >
          <BarChart3 className="h-4 w-4" />
          Full analytics
        </Link>
        <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-gray-600">
          Close
        </button>
      </div>
    </>
  );

  const templateSidePanel = (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-medium uppercase text-gray-400">Template</p>
        <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
          {previewTemplate?.displayName || templateName || 'No template assigned'}
        </p>
        {templateName ? (
          <p className="mt-0.5 font-mono text-[11px] text-gray-400">{templateName}</p>
        ) : null}
      </div>

      <div>
        <p className="text-xs font-medium uppercase text-gray-400">Template content</p>
        <div className="mt-1 rounded-lg bg-white p-3 text-sm leading-relaxed text-gray-800 shadow-sm dark:bg-gray-900 dark:text-gray-200">
          {previewTemplate?.bodyPreview || data.sampleMessage || '—'}
        </div>
      </div>

      <div>
        <p className="text-xs font-medium uppercase text-gray-400">Deep links</p>
        {deepLinkRefs.length ? (
          <div className="mt-1 space-y-2">
            {deepLinkRefs.map((ref, idx) => (
              <div
                key={`${ref.label}-${idx}`}
                className="rounded-lg border border-gray-200 bg-white p-3 text-xs dark:border-gray-700 dark:bg-gray-900"
              >
                <p className="font-medium text-gray-900 dark:text-white">{ref.label}</p>
                <p className="mt-1 text-gray-600 dark:text-gray-300">
                  {ref.campaign?.name || 'Unknown campaign'}
                </p>
                {ref.url ? (
                  <p className="mt-1 break-all text-blue-700 dark:text-blue-300">{ref.url}</p>
                ) : (
                  <p className="mt-1 text-amber-700 dark:text-amber-300">Link unavailable</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-xs text-gray-500">No deep link mapped on this notification.</p>
        )}
      </div>

      {previewTemplate?.buttons?.length ? (
        <WhatsAppTemplateButtonsPreview
          buttons={previewTemplate.buttons}
          campaignsById={campaignsById}
        />
      ) : null}

      {templatePreviewProps ? (
        <div className="flex flex-col items-center pt-2">
          <WhatsAppDevicePreview
            message={templatePreviewProps.message}
            headerText={templatePreviewProps.headerText}
            footer={templatePreviewProps.footer}
            buttons={templatePreviewProps.buttons}
            headerMediaUrl={templatePreviewProps.headerMediaUrl}
            headerMimeType={templatePreviewProps.headerMimeType}
          />
          <p className="mt-3 max-w-xs text-center text-[11px] text-gray-500">
            Sample values from variable mappings — actual message uses live notification data.
          </p>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
        <div className="sticky top-0 flex shrink-0 items-start justify-between border-b border-gray-200 bg-white px-5 py-4 dark:border-gray-700 dark:bg-gray-900">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {data.pushTitle || data.label}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{data.sampleMessage}</p>
            <p className="mt-0.5 font-mono text-[10px] text-gray-400">
              {data.fcmType} · {data.eventKey}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[minmax(0,1.1fr)_minmax(280px,0.9fr)]">
            <div className="space-y-5 overflow-y-auto p-5">{detailsBody}</div>
            <div className="overflow-y-auto border-t border-gray-200 bg-gray-50/80 p-5 dark:border-gray-700 dark:bg-gray-950/40 lg:border-l lg:border-t-0">
              {templateSidePanel}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateEditModal({
  item,
  templates,
  onClose,
  onSaved,
}: {
  item: WhatsAppNotificationEvent;
  templates: WhatsAppTemplate[];
  onClose: () => void;
  onSaved: (patch: Partial<WhatsAppNotificationEvent>) => void;
}) {
  const blankMappings = useCallback((count: number): WhatsAppTemplateVariableMapping[] => {
    const total = Math.max(Number(count) || 0, 0);
    if (total <= 0) return [];
    return Array.from({ length: total }, (_, i) => ({
      slot: i + 1,
      label: `Variable ${i + 1}`,
      contextKey: '',
      sourceType: 'context' as const,
      deepLinkCampaignId: null,
      deepLinkUrlType: 'short' as const,
      deepLinkParamBindings: {},
    }));
  }, []);

  const trimMappingsToCount = useCallback(
    (
      mappings: WhatsAppTemplateVariableMapping[] | undefined,
      count: number
    ): WhatsAppTemplateVariableMapping[] | undefined => {
      if (!Array.isArray(mappings) || count <= 0) return mappings;
      return mappings
        .filter((m) => Number(m?.slot) > 0 && Number(m.slot) <= count)
        .map((m) => ({ ...m, slot: Number(m.slot) }));
    },
    []
  );

  const activeTemplates = useMemo(() => getActiveTemplates(templates), [templates]);
  const activeNames = useMemo(() => activeTemplateNameSet(templates), [templates]);
  const activeTemplateByName = useMemo(() => buildActiveTemplateMap(templates), [templates]);

  const [contextFields, setContextFields] = useState<WhatsAppContextField[]>(
    () => mergeNotificationFields(item.availableContextFields || [])
  );
  const [fieldsLoading, setFieldsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setFieldsLoading(true);

    const loadFields = async () => {
      const merged = new Map<string, WhatsAppContextField>();
      const add = (fields?: WhatsAppContextField[] | null) => {
        for (const f of fields || []) {
          if (f?.key && !merged.has(f.key)) merged.set(f.key, f);
        }
      };

      add(item.availableContextFields);

      try {
        const detailRes = await fetchWhatsAppNotificationDetail(item.eventKey, { period: 'monthly' });
        add(detailRes.data?.availableContextFields);
      } catch {
        /* keep whatever we have */
      }

      try {
        const settingsRes = await fetchWhatsAppHubSettings();
        add(settingsRes.data?.referenceGuide?.variableCatalog?.allContextFields);
      } catch {
        /* keep whatever we have */
      }

      for (const key of item.bodyValueKeys || []) {
        const k = String(key || '').trim();
        if (!k || merged.has(k)) continue;
        merged.set(k, {
          key: k,
          label: k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim(),
          sourceType: 'context',
        });
      }

      if (!cancelled) {
        setContextFields(mergeNotificationFields(Array.from(merged.values())));
        setFieldsLoading(false);
      }
    };

    void loadFields();
    return () => {
      cancelled = true;
    };
  }, [item.eventKey, item.availableContextFields, item.bodyValueKeys]);

  const [templateName, setTemplateName] = useState(
    () => resolveAssignedTemplateName(item, activeNames) || ''
  );
  const [templateRules, setTemplateRules] = useState<WhatsAppTemplateRule[]>(
    () => item.templateRules || []
  );
  const selectedTemplate = templateName ? activeTemplateByName.get(templateName) : undefined;
  const slotInfo = useMemo(
    () => parseConsecutiveTemplateSlots(selectedTemplate?.bodyPreview || ''),
    [selectedTemplate?.bodyPreview]
  );
  const templateVariableCount = Math.max(
    slotInfo.count,
    selectedTemplate?.variableCount ?? 0,
    selectedTemplate?.defaultVariableMappings?.length ?? 0
  );
  const fallbackVariableCount = Math.max(
    item.templateVariableMappings?.length ?? 0,
    item.bodyValueKeys?.length ?? 0
  );
  const variableCount = templateName ? templateVariableCount : fallbackVariableCount;

  const [variableMappings, setVariableMappings] = useState<WhatsAppTemplateVariableMapping[]>(() =>
    buildVariableMappings(variableCount, blankMappings(variableCount), contextFields)
  );
  const [whatsappCtaConfig, setWhatsappCtaConfig] = useState<WhatsAppCtaDeepLinkConfig | null>(
    item.whatsappCtaConfig || null
  );
  const [headerMediaUrl, setHeaderMediaUrl] = useState<string | null>(item.headerMediaUrl || null);
  const [headerMimeType, setHeaderMimeType] = useState<string | null>(item.headerMimeType || null);
  const [headerFileName, setHeaderFileName] = useState<string | null>(item.headerFileName || null);
  const [deepLinkCampaigns, setDeepLinkCampaigns] = useState<DeepLinkCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCampaignsLoading(true);
    fetchDeepLinkCampaigns()
      .then(setDeepLinkCampaigns)
      .catch(() => setDeepLinkCampaigns([]))
      .finally(() => setCampaignsLoading(false));
  }, []);

  const campaignsById = useMemo(
    () => new Map(deepLinkCampaigns.map((c) => [c.id, c])),
    [deepLinkCampaigns]
  );

  const templatePreviewProps = useMemo(() => {
    if (!selectedTemplate) return null;
    const base = notificationPreviewFromTemplate(selectedTemplate, variableMappings, campaignsById);
    return {
      ...base,
      headerMediaUrl: headerMediaUrl || base.headerMediaUrl,
      headerMimeType: headerMimeType || base.headerMimeType,
    };
  }, [selectedTemplate, variableMappings, campaignsById, headerMediaUrl, headerMimeType]);

  useEffect(() => {
    const assignedName = item.templateName?.trim() || '';
    const useEventMappings =
      Boolean(templateName) &&
      templateName === assignedName &&
      Boolean(item.templateVariableMappings?.length);
    const source = resolveMappingSource(
      useEventMappings ? item.templateVariableMappings : undefined,
      selectedTemplate?.defaultVariableMappings
    );
    const targetCount = templateName ? templateVariableCount : variableCount;
    const trimmed = trimMappingsToCount(source, targetCount);
    const sourceOrBlank =
      trimmed && trimmed.length > 0 ? trimmed : blankMappings(targetCount);
    setVariableMappings(buildVariableMappings(targetCount, sourceOrBlank, contextFields));
    setWhatsappCtaConfig(item.whatsappCtaConfig || null);
  }, [
    templateName,
    templateVariableCount,
    variableCount,
    item.eventKey,
    item.templateName,
    item.templateVariableMappings,
    item.whatsappCtaConfig,
    contextFields,
    selectedTemplate?.defaultVariableMappings,
    blankMappings,
    trimMappingsToCount,
  ]);

  const clearEventMappings = () => {
    const targetCount = templateName ? templateVariableCount : variableCount;
    setVariableMappings(blankMappings(targetCount));
    toast.success('Cleared event mappings for this template');
  };

  const handleSave = async () => {
    const invalidDeepLink = variableMappings.some(
      (m) => m.sourceType === 'deep_link' && !m.deepLinkCampaignId
    );
    if (invalidDeepLink) {
      toast.error('Select a deep link campaign for all deep link variables');
      return;
    }

    setSaving(true);
    try {
      const assignedName = resolveAssignedTemplateName(item, activeNames) || '';
      const previousTemplateSig = `${assignedName}::${(item.templateRules || [])
        .filter((r) => r.enabled !== false)
        .map((r) => r.templateName || '')
        .join('|')}`;
      const nextTemplateSig = `${templateName || ''}::${templateRules
        .filter((r) => r.enabled !== false)
        .map((r) => r.templateName || '')
        .join('|')}`;
      const previousLinkSig = `${(item.templateVariableMappings || [])
        .filter((m) => m.sourceType === 'deep_link')
        .map((m) => `${m.slot}:${m.deepLinkCampaignId || ''}`)
        .join('|')}::${item.whatsappCtaConfig?.deepLinkCampaignId || ''}`;
      const nextCta = whatsappCtaConfig?.deepLinkCampaignId ? whatsappCtaConfig : null;
      const nextLinkSig = `${variableMappings
        .filter((m) => m.sourceType === 'deep_link')
        .map((m) => `${m.slot}:${m.deepLinkCampaignId || ''}`)
        .join('|')}::${nextCta?.deepLinkCampaignId || ''}`;
      const healthReset: NotificationHealthPatch = {};
      if (previousTemplateSig !== nextTemplateSig) {
        healthReset.templateWorking = null;
        healthReset.linkNavigationWorking = null;
      } else if (previousLinkSig !== nextLinkSig) {
        healthReset.linkNavigationWorking = null;
      }

      await updateWhatsAppNotificationEvent(item.eventKey, {
        templateName: templateName || null,
        templateRules,
        templateVariableMappings: variableMappings,
        whatsappCtaConfig: nextCta,
        headerMediaUrl,
        headerMimeType,
        headerFileName,
        ...healthReset,
      });
      const resetNote =
        healthReset.templateWorking === null || healthReset.linkNavigationWorking === null
          ? ' Re-test template / link health after this change.'
          : '';
      toast.success(`Template updated.${resetNote}`);
      onSaved({
        templateName: templateName || null,
        templateRules,
        templateVariableMappings: variableMappings,
        whatsappCtaConfig: nextCta,
        headerMediaUrl,
        headerMimeType,
        headerFileName,
        ...healthReset,
        ...(healthReset.templateWorking === null
          ? { templateHealthCheckedAt: null, linkHealthCheckedAt: null }
          : healthReset.linkNavigationWorking === null
            ? { linkHealthCheckedAt: null }
            : {}),
      });
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const addCampaign = (campaign: DeepLinkCampaign) => {
    setDeepLinkCampaigns((prev) => {
      if (prev.some((c) => c.id === campaign.id)) return prev;
      return [campaign, ...prev];
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex shrink-0 items-start justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold">Assign WhatsApp template</h2>
            <p className="mt-1 font-medium text-gray-900 dark:text-white">{item.pushTitle || item.label}</p>
            <p className="mt-0.5 text-sm text-gray-500">{item.sampleMessage}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[1fr_320px]">
          <div className="overflow-y-auto p-5">
        <label className="block text-xs font-medium uppercase text-gray-400">
          Default Interakt template (fallback)
        </label>
        <select
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        >
          <option value="">Select template…</option>
          {activeTemplates.map((t) => (
            <option key={t.name} value={t.name}>
              {t.displayName || t.name} ({Math.max(t.variableCount || 0, parseConsecutiveTemplateSlots(t.bodyPreview || '').count)} vars)
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Used when no conditional rule matches (e.g. Instant). For Online vs Offline, add rules below.
          Changing the template or links resets QA health to Untested.
        </p>

        {selectedTemplate?.headerFormat && (
          <div className="mt-4">
            <WhatsAppMediaHeaderUploader
              headerFormat={selectedTemplate.headerFormat}
              headerMediaUrl={headerMediaUrl}
              headerMimeType={headerMimeType}
              headerFileName={headerFileName}
              onChange={(media) => {
                setHeaderMediaUrl(media.headerMediaUrl);
                setHeaderMimeType(media.headerMimeType);
                setHeaderFileName(media.headerFileName);
              }}
            />
          </div>
        )}

        <div className="mt-5 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium">Conditional templates</p>
              <p className="text-xs text-gray-500">
                First matching rule wins. Example: Online → online template, Offline → offline template.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setTemplateRules((prev) => [
                  ...prev,
                  newTemplateRule({
                    label: prev.length === 0 ? 'Online' : 'Offline',
                    conditions: [
                      {
                        field: 'consultationType',
                        operator: 'contains',
                        value: prev.length === 0 ? 'Online' : 'Offline',
                      },
                    ],
                  }),
                ])
              }
              className="shrink-0 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium dark:border-gray-600"
            >
              Add rule
            </button>
          </div>

          {templateRules.length === 0 ? (
            <p className="mt-3 text-xs text-gray-400">No conditional rules yet.</p>
          ) : (
            <div className="mt-3 space-y-3">
              {templateRules.map((rule, index) => (
                <RuleTemplateBlock
                  key={rule.id}
                  rule={rule}
                  index={index}
                  activeTemplates={activeTemplates}
                  activeTemplateByName={activeTemplateByName}
                  contextFields={contextFields}
                  fieldsLoading={fieldsLoading}
                  deepLinkCampaigns={deepLinkCampaigns}
                  campaignsLoading={campaignsLoading}
                  onCampaignCreated={addCampaign}
                  onChangeRule={(i, next) =>
                    setTemplateRules((prev) => prev.map((r, idx) => (idx === i ? next : r)))
                  }
                  onRemove={(i) => setTemplateRules((prev) => prev.filter((_, idx) => idx !== i))}
                />
              ))}
            </div>
          )}
        </div>

        {selectedTemplate?.bodyPreview && (
          <div className="mt-4">
            <p className="text-xs font-medium uppercase text-gray-400">Default template preview</p>
            <p className="mt-1 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm leading-relaxed dark:bg-gray-800">
              {selectedTemplate.bodyPreview}
            </p>
          </div>
        )}

        {selectedTemplate?.buttons?.length ? (
          <WhatsAppTemplateButtonsPreview
            className="mt-4"
            buttons={selectedTemplate.buttons}
            campaignsById={campaignsById}
          />
        ) : null}

        {templateName && (
          <div className="mt-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium">Template variables</p>
              <button
                type="button"
                onClick={clearEventMappings}
                className="rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Clear mappings
              </button>
            </div>
            <p className="mb-2 text-xs text-gray-500">
              {(templateName ? templateVariableCount : variableCount)} variable
              {(templateName ? templateVariableCount : variableCount) === 1 ? '' : 's'}
            </p>
            {selectedTemplate?.defaultVariableMappings?.length &&
              !item.templateVariableMappings?.length && (
                <p className="mb-2 text-xs text-[#128C7E]">
                  Loaded from template defaults — save here only if you need to override this event.
                </p>
              )}
            {!slotInfo.valid && slotInfo.count > 0 && (
              <p className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
                Body placeholders should be consecutive {'{{1}}'}…{'{{' + slotInfo.count + '}}'}.
                Missing: {slotInfo.gaps.map((g) => `{{${g}}}`).join(', ')}
              </p>
            )}
            {fieldsLoading ? (
              <p className="text-sm text-gray-500">Loading field names…</p>
            ) : (
              <>
                {campaignsLoading && (
                  <p className="mb-2 text-xs text-gray-400">Loading deep link campaigns…</p>
                )}
                <WhatsAppTemplateVariableMappingEditor
                  mappings={variableMappings}
                  fields={contextFields}
                  campaigns={deepLinkCampaigns}
                  onCampaignCreated={addCampaign}
                  onChange={setVariableMappings}
                />
                {!contextFields.length && variableCount > 0 && (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-200">
                    No context fields loaded. You can still use static text or deep links, or refresh Settings.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <div className="mt-5 flex gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
          <button
            type="button"
            disabled={
              saving ||
              (!templateName &&
                !templateRules.some((r) => r.enabled !== false && Boolean(r.templateName?.trim())))
            }
            onClick={() => void handleSave()}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-gray-600">
            Cancel
          </button>
        </div>
          </div>

          {templatePreviewProps && (
            <div className="flex shrink-0 flex-col items-center overflow-y-auto border-t border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-950/40 lg:border-l lg:border-t-0">
              <WhatsAppDevicePreview
                message={templatePreviewProps.message}
                headerText={templatePreviewProps.headerText}
                footer={templatePreviewProps.footer}
                buttons={templatePreviewProps.buttons}
                headerMediaUrl={templatePreviewProps.headerMediaUrl}
                headerMimeType={templatePreviewProps.headerMimeType}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const WhatsAppNotificationsPanel: React.FC = () => {
  const [catalog, setCatalog] = useState<WhatsAppNotificationCatalog | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [statsQuery, setStatsQuery] = useState<WhatsAppStatsQuery>({ period: 'monthly' });
  const [tab, setTab] = useState<AudienceTab>('user');
  const [service, setService] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [wiredFilter, setWiredFilter] = useState<'all' | 'wired' | 'not_wired'>('all');
  const [healthFilter, setHealthFilter] = useState<HealthFilter>('all');
  const [viewItem, setViewItem] = useState<WhatsAppNotificationEvent | null>(null);
  const [templateItem, setTemplateItem] = useState<WhatsAppNotificationEvent | null>(null);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [healthSavingKey, setHealthSavingKey] = useState<string | null>(null);
  const catalogLoadedRef = useRef(false);
  const staleDisableRef = useRef(new Set<string>());

  const activeNames = useMemo(() => activeTemplateNameSet(templates), [templates]);
  const activeTemplateByName = useMemo(() => buildActiveTemplateMap(templates), [templates]);

  const applyNotificationPatch = useCallback(
    (eventKey: string, patch: Partial<WhatsAppNotificationEvent>) => {
      setCatalog((prev) => (prev ? patchCatalogNotification(prev, eventKey, patch) : prev));
      setViewItem((prev) => (prev?.eventKey === eventKey ? { ...prev, ...patch } : prev));
      setTemplateItem((prev) => (prev?.eventKey === eventKey ? { ...prev, ...patch } : prev));
    },
    []
  );

  const loadCatalog = useCallback(async () => {
    if (!isCustomStatsReady(statsQuery)) return;
    const silent = catalogLoadedRef.current;
    if (!silent) setCatalogLoading(true);
    try {
      const catalogRes = await fetchWhatsAppNotificationCatalog(buildStatsQueryParams(statsQuery));
      setCatalog(catalogRes.data);
      catalogLoadedRef.current = true;
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load notifications'));
      if (!silent) setCatalog(null);
    } finally {
      if (!silent) setCatalogLoading(false);
    }
  }, [statsQuery]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const templatesRes = await fetchWhatsAppTemplateList();
      setTemplates(templatesRes.data || []);
    } catch {
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (templatesLoading || !catalog) return;
    const candidates = catalog.items.filter((i) => !String(i.eventKey || '').startsWith('RETARGET_'));
    const stale = candidates.filter(
      (item) => item.whatsappEnabled && !hasAssignableWhatsAppTemplate(item, activeNames)
    );
    const toFix = stale.filter((item) => !staleDisableRef.current.has(item.eventKey));
    if (!toFix.length) return;
    toFix.forEach((item) => staleDisableRef.current.add(item.eventKey));
    void (async () => {
      let disabled = 0;
      for (const item of toFix) {
        try {
          await updateWhatsAppNotificationEvent(item.eventKey, { whatsappEnabled: false });
          applyNotificationPatch(item.eventKey, { whatsappEnabled: false, enabled: false });
          disabled += 1;
        } catch {
          /* keep in-flight cleared below so we can retry */
        } finally {
          staleDisableRef.current.delete(item.eventKey);
        }
      }
      if (disabled > 0) {
        toast.error(
          `WhatsApp turned off for ${disabled} notification${disabled === 1 ? '' : 's'} with a missing or unassigned template`
        );
      }
    })();
  }, [templatesLoading, catalog, activeNames, applyNotificationPatch]);

  const tabCounts = useMemo(() => {
    if (!catalog) return { user: 0, service_provider: 0 };
    const nonRetarget = catalog.items.filter((i) => !String(i.eventKey || '').startsWith('RETARGET_'));
    const userCount = nonRetarget.filter((i) => i.recipientType === 'user').length;
    const providerCount = nonRetarget.filter((i) => isServiceProviderRecipient(i.recipientType)).length;
    return { user: userCount, service_provider: providerCount };
  }, [catalog]);

  const items = useMemo(() => {
    if (!catalog) return [];
    let list = catalog.items.filter((i) => !String(i.eventKey || '').startsWith('RETARGET_'));
    if (tab === 'user') {
      list = list.filter((i) => i.recipientType === 'user');
    } else {
      list = list.filter((i) => isServiceProviderRecipient(i.recipientType));
    }
    if (service) list = list.filter((i) => i.service === service);
    if (category) list = list.filter((i) => i.category === category);
    if (enabledFilter === 'enabled') list = list.filter((i) => i.whatsappEnabled);
    if (enabledFilter === 'disabled') list = list.filter((i) => !i.whatsappEnabled);
    if (wiredFilter === 'wired') list = list.filter((i) => Boolean(i.whatsappWired));
    if (wiredFilter === 'not_wired') list = list.filter((i) => !i.whatsappWired);
    if (healthFilter !== 'all') list = list.filter((i) => matchesHealthFilter(i, healthFilter));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (i) =>
          (i.pushTitle || i.label).toLowerCase().includes(q) ||
          i.sampleMessage.toLowerCase().includes(q) ||
          (i.fcmType && i.fcmType.toLowerCase().includes(q)) ||
          i.service.toLowerCase().includes(q)
      );
    }
    return list;
  }, [catalog, tab, service, category, search, enabledFilter, wiredFilter, healthFilter]);

  const qaNeedsCount = useMemo(() => {
    if (!catalog) return 0;
    return catalog.items.filter(
      (i) =>
        !String(i.eventKey || '').startsWith('RETARGET_') &&
        (matchesHealthFilter(i, 'untested') || matchesHealthFilter(i, 'issues'))
    ).length;
  }, [catalog]);

  const handleToggle = async (item: WhatsAppNotificationEvent, enabled: boolean) => {
    if (togglingKey === item.eventKey) return;

    if (enabled && !hasAssignableWhatsAppTemplate(item, activeNames)) {
      toast.error('Assign an approved template before enabling WhatsApp');
      setTemplateItem(item);
      return;
    }

    const previous = item.whatsappEnabled;
    setTogglingKey(item.eventKey);
    applyNotificationPatch(item.eventKey, { whatsappEnabled: enabled, enabled });

    try {
      await updateWhatsAppNotificationEvent(item.eventKey, { whatsappEnabled: enabled });
      toast.success(enabled ? 'WhatsApp enabled' : 'WhatsApp disabled');
    } catch (err: unknown) {
      applyNotificationPatch(item.eventKey, { whatsappEnabled: previous, enabled: previous });
      toast.error(getApiErrorMessage(err, 'Toggle failed'));
    } finally {
      setTogglingKey(null);
    }
  };

  const handleHealthChange = async (item: WhatsAppNotificationEvent, patch: NotificationHealthPatch) => {
    const key = `${item.eventKey}:${patch.templateWorking !== undefined ? 'template' : 'link'}`;
    if (healthSavingKey === key) return;
    const previous = {
      templateWorking: item.templateWorking ?? null,
      linkNavigationWorking: item.linkNavigationWorking ?? null,
      templateHealthCheckedAt: item.templateHealthCheckedAt ?? null,
      linkHealthCheckedAt: item.linkHealthCheckedAt ?? null,
    };
    const next = {
      ...patch,
      ...(patch.templateWorking !== undefined
        ? {
            templateHealthCheckedAt:
              patch.templateWorking === null ? null : new Date().toISOString(),
          }
        : {}),
      ...(patch.linkNavigationWorking !== undefined
        ? {
            linkHealthCheckedAt:
              patch.linkNavigationWorking === null ? null : new Date().toISOString(),
          }
        : {}),
    };
    setHealthSavingKey(key);
    applyNotificationPatch(item.eventKey, next);
    try {
      await updateWhatsAppNotificationEvent(item.eventKey, patch);
    } catch (err: unknown) {
      applyNotificationPatch(item.eventKey, previous);
      toast.error(getApiErrorMessage(err, 'Failed to save health'));
    } finally {
      setHealthSavingKey(null);
    }
  };

  const filterClass =
    'w-full min-w-0 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 sm:w-auto';

  return (
    <WhatsAppPanelShell
      title="Notifications"
      description="All push notifications — configure WhatsApp templates, toggles, and QA health per event."
      actions={
        <WhatsAppStatsPeriodSelector value={statsQuery} onChange={setStatsQuery} />
      }
    >
      {catalog && (
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs text-gray-500">Total events</p>
            <p className="text-xl font-bold">{catalog.totals.events}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs text-gray-500">WhatsApp enabled</p>
            <p className="text-xl font-bold">{catalog.totals.enabledEvents}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs text-gray-500">{statsMessagesLabel(statsQuery)}</p>
            <p className="text-xl font-bold">{catalog.totals.totalMessages}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs text-gray-500">Est. cost</p>
            <p className="text-xl font-bold">{formatInr(catalog.totals.estimatedCostInr)}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs text-gray-500">Needs QA</p>
            <p className="text-xl font-bold">{qaNeedsCount}</p>
          </div>
        </div>
      )}

      <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-6">
          {(
            [
              { id: 'user' as const, label: 'Users' },
              { id: 'service_provider' as const, label: 'Service Providers' },
            ] as const
          ).map((t) => {
            const count = tabCounts[t.id];
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`-mb-px border-b-2 px-1 pb-3 text-sm font-medium transition ${
                  active
                    ? 'border-gray-900 text-gray-900 dark:border-gray-100 dark:text-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {t.label}
                <span className="ml-1.5 text-xs font-normal opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, message, service…"
            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800"
          />
        </div>
        <select value={service} onChange={(e) => setService(e.target.value)} className={filterClass}>
          <option value="">All services</option>
          {(catalog?.filters.services || []).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={filterClass}>
          <option value="">All types</option>
          {(catalog?.filters.categories || []).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={enabledFilter}
          onChange={(e) => setEnabledFilter(e.target.value as 'all' | 'enabled' | 'disabled')}
          className={filterClass}
        >
          <option value="all">All WhatsApp status</option>
          <option value="enabled">Enabled only</option>
          <option value="disabled">Disabled only</option>
        </select>
        <select
          value={wiredFilter}
          onChange={(e) => setWiredFilter(e.target.value as 'all' | 'wired' | 'not_wired')}
          className={filterClass}
        >
          <option value="all">All wiring</option>
          <option value="wired">Wired (backend send live)</option>
          <option value="not_wired">Not wired yet</option>
        </select>
        <select
          value={healthFilter}
          onChange={(e) => setHealthFilter(e.target.value as HealthFilter)}
          className={filterClass}
        >
          <option value="all">All QA health</option>
          <option value="untested">Untested</option>
          <option value="working">Template + link working</option>
          <option value="issues">Has issues</option>
        </select>
      </div>

      {catalogLoading && !catalog ? (
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">No notifications match your filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left dark:bg-gray-800/80">
              <tr>
                <th className="px-4 py-2.5 font-medium">Notification</th>
                <th className="px-4 py-2.5 font-medium">Service</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium">Template</th>
                <th className="px-4 py-2.5 font-medium">WhatsApp</th>
                <th className="px-4 py-2.5 font-medium">QA health</th>
                <th className="px-4 py-2.5 font-medium">Usage</th>
                <th className="px-4 py-2.5 font-medium w-12" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const templateCategory = resolveAssignedTemplateCategory(item, activeNames, activeTemplateByName);
                return (
                <tr key={item.eventKey} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-white">{item.pushTitle || item.label}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">{item.sampleMessage}</p>
                    <p className="mt-1 font-mono text-[10px] text-gray-400">{item.fcmType}</p>
                    {item.whatsappWired ? (
                      <p className="mt-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                        Backend send live
                      </p>
                    ) : (
                      <p className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">
                        Listed for FCM — WhatsApp send not wired yet
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{item.service}</td>
                  <td className="px-4 py-3">
                    {templateCategory ? (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-800">
                        {templateCategory}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="w-[240px] max-w-[240px] px-4 py-3 align-top">
                    {(() => {
                      const assigned = assignedTemplateLabel(item, activeNames);
                      return (
                        <span
                          className={`block truncate text-xs ${
                            assigned.missing
                              ? 'text-amber-700 dark:text-amber-300'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                          title={assigned.text}
                        >
                          {assigned.text}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <ToggleSwitch
                      checked={item.whatsappEnabled && hasAssignableWhatsAppTemplate(item, activeNames)}
                      disabled={
                        togglingKey === item.eventKey ||
                        templatesLoading ||
                        (!item.whatsappEnabled && !hasAssignableWhatsAppTemplate(item, activeNames))
                      }
                      title={
                        !hasAssignableWhatsAppTemplate(item, activeNames)
                          ? 'Assign an approved template before enabling WhatsApp'
                          : undefined
                      }
                      onChange={(v) => void handleToggle(item, v)}
                    />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <NotificationHealthCell
                      item={item}
                      disabled={healthSavingKey?.startsWith(`${item.eventKey}:`)}
                      onChange={(patch) => void handleHealthChange(item, patch)}
                    />
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <p>{item.stats.billableCount} sent</p>
                    <p className="text-gray-500">{formatInr(item.stats.estimatedCostInr)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <RowActionsMenu
                      item={item}
                      toggling={togglingKey === item.eventKey}
                      canEnable={hasAssignableWhatsAppTemplate(item, activeNames)}
                      onView={() => setViewItem(item)}
                      onTemplate={() => setTemplateItem(item)}
                      onToggle={(v) => void handleToggle(item, v)}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewItem && (
        <NotificationViewModal
          item={viewItem}
          templates={templates}
          statsQuery={statsQuery}
          onStatsQueryChange={setStatsQuery}
          onClose={() => setViewItem(null)}
          onSaved={(patch) => {
            applyNotificationPatch(viewItem.eventKey, patch);
          }}
        />
      )}

      {templateItem && (
        <TemplateEditModal
          item={templateItem}
          templates={templates}
          onClose={() => setTemplateItem(null)}
          onSaved={(patch) => {
            applyNotificationPatch(templateItem.eventKey, patch);
            setTemplateItem(null);
          }}
        />
      )}

      {templatesLoading && templates.length === 0 && (
        <p className="sr-only">Loading templates</p>
      )}
    </WhatsAppPanelShell>
  );
};

export {
  ToggleSwitch as WhatsAppNotificationToggleSwitch,
  RowActionsMenu as WhatsAppNotificationRowActionsMenu,
  NotificationViewModal as WhatsAppNotificationViewModal,
  TemplateEditModal as WhatsAppNotificationTemplateEditModal,
  NotificationHealthCell as WhatsAppNotificationHealthCell,
  formatInr as formatWhatsAppNotificationInr,
  resolveAssignedTemplateName,
  resolveAssignedTemplateCategory,
  hasAssignableWhatsAppTemplate,
  assignedTemplateLabel,
  patchCatalogNotification,
  activeTemplateNameSet,
  buildActiveTemplateMap,
  matchesHealthFilter,
};

export default WhatsAppNotificationsPanel;
